import { Helmet } from "react-helmet-async";

const DEFAULT_OG_IMAGE = "https://dynastytrademaker.com/brand/og-default.jpg";
const SITE_NAME = "Dynasty Trade Maker";
const SITE_URL = "https://dynastytrademaker.com";

const PAGE_SEO = {
  home: {
    title: "Dynasty Trade Maker — AI-Powered Dynasty Fantasy Football Trade Tool",
    description: "Find winning dynasty trades with AI. Real-time player values, trade simulator, draft war room, and contender rankings. Connect your Sleeper league instantly.",
    path: "/",
  },
  "how-it-works": {
    title: "How It Works — Dynasty Trade Maker",
    description: "Connect your Sleeper league in 5 easy steps. See real-time player values, get AI trade suggestions, simulate impact, and dominate your dynasty league.",
    path: "/how-it-works",
  },
  features: {
    title: "15+ Dynasty Trade Tools — Dynasty Trade Maker Features",
    description: "AI Trade Analyzer, Trade Finder, Simulator, Calculator, Draft War Room, Mock Draft, Contender Rankings, Roster Optimizer, and more. Built for dynasty fantasy football.",
    path: "/features",
  },
  faq: {
    title: "Frequently Asked Questions — Dynasty Trade Maker",
    description: "How does Dynasty Trade Maker work? What platforms are supported? How accurate are values? Get answers to all your dynasty trade tool questions.",
    path: "/faq",
  },
  guide: {
    title: "Dynasty Trading 101: The Complete Guide — Dynasty Trade Maker",
    description: "Master dynasty fantasy football trading. Learn player values, when to buy/sell, Superflex strategy, draft picks as currency, and the waiver wire advantage.",
    path: "/guide",
  },
  billing: {
    title: "Pricing — Dynasty Trade Maker Pro",
    description: "Start free, upgrade when ready. 1 League $19.99/yr, 3 Leagues $49.99/yr, Unlimited $79.99/yr. AI trade tools for dynasty fantasy football.",
    path: "/billing",
  },
  "trade-analyzer": {
    title: "AI Trade Analyzer — Dynasty Trade Maker",
    description: "Get 6 AI-powered dynasty trade suggestions. 3 modes, 3 strategies, value-balanced within 8%. The most powerful dynasty trade tool.",
    path: "/features/trade-analyzer",
  },
  "trade-finder": {
    title: "Trade Finder Auto-Scanner — Dynasty Trade Maker",
    description: "One-click scan of your entire roster against every team. 300+ combinations, instant results, zero AI cost. Find mutual-fit dynasty trades.",
    path: "/features/trade-finder",
  },
  "draft-room": {
    title: "Draft War Room & Mock Draft — Dynasty Trade Maker",
    description: "Pick-by-pick AI advice, trade-this-pick analysis, and full mock draft simulator for your dynasty rookie draft.",
    path: "/features/draft-room",
  },
  "trade-simulator": {
    title: "Trade Simulator — Dynasty Trade Maker",
    description: "See before/after impact of any trade: power ranking, positional grades, total value. Simulate before you propose.",
    path: "/features/trade-simulator",
  },
  "roster-tools": {
    title: "Player Compare, Roster Optimizer, Contender Rankings — Dynasty Trade Maker",
    description: "Compare players side-by-side, build an AI trade plan, and score every team on contender vs rebuilder scale.",
    path: "/features/roster-tools",
  },
};

export function FaqLd({ faqs }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(data)}</script></Helmet>;
}

export function BreadcrumbLd({ items }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.url}`,
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(data)}</script></Helmet>;
}

export default function SEO({ page, title, description, image, path }) {
  const seo = PAGE_SEO[page] || {};
  const pageTitle = title || seo.title || `${SITE_NAME} — Dynasty Fantasy Football Trade Tool`;
  const pageDesc = description || seo.description || "AI-powered dynasty fantasy football trade tool. Connect your Sleeper league and find winning trades.";
  const pageImage = image || DEFAULT_OG_IMAGE;
  const pageUrl = `${SITE_URL}${path || seo.path || "/"}`;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional */}
      <meta name="robots" content="index, follow" />
      <meta name="theme-color" content="#0a0a0a" />
    </Helmet>
  );
}

export { PAGE_SEO, SITE_URL, SITE_NAME };
